import { Base } from '@studiometa/js-toolkit';
import { gsap } from 'gsap';

const LOADER_STORAGE_KEY = 'decibulles_loader_seen';
const LOADER_EXPIRY_DAYS = 30;

/**
 * @typedef {LoaderRefs} LoaderRefs
 * @property {HTMLElement[]} img
 */

/**
 * Loader component.
 * @augments {Base<{$refs: LoaderRefs}>}
 */
export default class Loader extends Base {
  /**
   * Class config.
   * @type {import('@studiometa/js-toolkit').BaseConfig}
   */
  static config = {
    name: 'Loader',
    refs: ['img[]'],
  };

  /** @type {gsap.core.Timeline|null} */
  tl = null;

  /** @type {HTMLElement[]} */
  headerElements = [];

  /** @type {HTMLVideoElement|null} */
  videoPlayer = null;

  /** @type {HTMLElement|null} */
  homeLogo = null;

  /** @type {HTMLElement|null} */
  homeLogoContainer = null;

  /**
   * Check if loader was already seen within expiry period.
   * @returns {boolean}
   */
  hasBeenSeen() {
    try {
      const stored = localStorage.getItem(LOADER_STORAGE_KEY);
      if (!stored) return false;
      const expiryDate = new Date(stored);
      return expiryDate > new Date();
    } catch {
      return false;
    }
  }

  /**
   * Mark loader as seen for the expiry period.
   */
  markAsSeen() {
    try {
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + LOADER_EXPIRY_DAYS);
      localStorage.setItem(LOADER_STORAGE_KEY, expiryDate.toISOString());
    } catch {
      // Silently fail if localStorage is not available
    }
  }

  /**
   * Mounted lifecycle hook.
   */
  mounted() {
    this.headerElements = /** @type {HTMLElement[]} */ (gsap.utils.toArray('.header__elements'));
    this.homeLogo = document.querySelector('#home-logo-icon');
    this.homeLogoContainer = document.querySelector('#homeLogoContainer');

    if (this.hasBeenSeen()) {
      gsap.set(this.headerElements, { opacity: 1 });
      gsap.set(this.$refs.img, { opacity: 0 });

      if (this.homeLogoContainer) {
        this.homeLogoContainer.classList.remove('text-green-dark');
        this.homeLogoContainer.classList.add('text-white');
      }

      if (this.homeLogo) {
        gsap.set(this.homeLogo, { y: 0 });
      }

      this.$destroy();
      return;
    }

    this.videoPlayer = document.querySelector('#video-player');
    document.body.classList.add('overflow-hidden');

    gsap.config({ force3D: true });

    this.tl = gsap.timeline({
      onComplete: () => {
        this.markAsSeen();
        this.$destroy();
      },
    });

    if (this.homeLogo) {
      this.tl.to(this.homeLogo, {
        y: 0,
        duration: 0.8,
        ease: 'back.inOut',
      });
    }

    this.tl.to(this.$refs.img, {
      opacity: 0,
      duration: 1,
      delay: 1.2,
      ease: 'power4.inOut',
      onStart: () => {
        if (this.videoPlayer) {
          this.videoPlayer.play();
        }

        if (this.homeLogoContainer) {
          gsap.to(this.homeLogoContainer, {
            color: 'white',
            duration: 0.5,
            ease: 'power2.inOut',
          });
        }
      },
    });

    this.tl.to(
      this.headerElements,
      {
        opacity: 1,
        duration: 1,
        ease: 'sine.inOut',
        onComplete: () => {
          document.body.classList.remove('overflow-hidden');
        },
      },
      '=-0.7',
    );
  }

  /**
   * Destroyed lifecycle hook.
   */
  destroyed() {
    this.tl?.kill();
    this.$el.remove();
  }
}
